import {
    type PromiseState,
    promiseStateRender,
    safePromiseStateTrigger,
    promiseStateCancel,
    promiseStateMaybeGet,
    promiseStateMustGet,
    promiseStateMaybeGetError,
    promiseStateMustGetError,
    promiseStateUpdate,
    initialPromiseState,
    promiseStateRenderAll,
    promiseStateReset,
    type PromiseStateRenderOptions,
} from './PromiseState'

import * as React from 'react'
import type { CommonDeps } from '../base/CommonDeps'
import type { StateLens } from './react'

export type PromiseManager<V> = {
    trigger(fn: () => Promise<V>): Promise<V>
    cancel(): void
    render(
        render: (value: V) => React.ReactElement<unknown>,
        opts?: PromiseStateRenderOptions
    ): React.ReactElement<unknown>
    maybeGet(): V | null
    mustGet(): V
    maybeGetError(): Error | null
    mustGetError(): Error
    update(updater: (value: V) => V): Promise<void>
    isInProgress(): boolean
    hasResolved(): boolean
    hasBeenRejected(): boolean
    setState(newState: PromiseState<V>): void
    getState(): PromiseState<V>
    getDeps(): CommonDeps
    reset(): void
}

/**
 * Create a new `PromiseState`. See the [docs] for more info.
 *
 * [docs]: ./../../../app/docs/RenderingPromises.md
 *
 * @param getDeps A getter function that returns the `CommonDeps` to be used
 * @param lens A lens looking at a given `PromiseState`
 * @param loadingState [Optional] A function returning an element to render when
 *                     the `PromiseManager` is still loading
 * @returns A new `PromiseManager`
 */
export function createPromiseManager<V>(
    getDeps: () => CommonDeps,
    lens: StateLens<PromiseState<V>>
): PromiseManager<V> {
    return {
        trigger: (fn) => safePromiseStateTrigger(getDeps(), lens, fn),
        cancel: () => promiseStateCancel(lens.get()),
        reset: () => promiseStateReset(lens),
        render: (fn, opts) => promiseStateRender(getDeps(), lens.get(), fn, opts),
        maybeGet: () => promiseStateMaybeGet(lens.get()),
        mustGet: () => promiseStateMustGet(lens.get()),
        maybeGetError: () => promiseStateMaybeGetError(lens.get()),
        mustGetError: () => promiseStateMustGetError(lens.get()),
        update: (fn) => lens.set(promiseStateUpdate(lens.get(), fn)),
        isInProgress: () => lens.get().status === 'in-progress',
        hasResolved: () => lens.get().status === 'resolved',
        hasBeenRejected: () => lens.get().status === 'rejected',
        setState: (newState) => lens.set(newState),
        getState: () => lens.get(),
        getDeps,
    }
}

export function cancelPromiseManagers<D extends { [k in keyof D]: PromiseManager<unknown> }>(dict: D) {
    for (const key in dict) {
        dict[key].cancel()
    }
}

export type DepromiseManagerAll<PromiseManagers> = PromiseManagers extends [PromiseManager<infer T1>, ...infer Rest]
    ? [T1, ...DepromiseManagerAll<Rest>]
    : []

export function renderPromiseManagers<PromiseManagers extends Array<PromiseManager<unknown>>>(
    managers: PromiseManagers,
    render: (values: DepromiseManagerAll<PromiseManagers>) => React.ReactElement<unknown>,
    opts?: PromiseStateRenderOptions
): React.ReactElement<unknown> {
    return promiseStateRenderAll(
        managers[0].getDeps(),
        managers.map((managers) => managers.getState()) as any,
        render,
        opts
    )
}

export function usePromiseManager<V>(deps: CommonDeps): PromiseManager<V> & { promiseState: PromiseState<V> } {
    const [promiseState, setPromiseState] = React.useState<PromiseState<V>>(initialPromiseState)

    const promiseStateRef = React.useRef<PromiseState<V>>(promiseState)
    promiseStateRef.current = promiseState

    const depsRef = React.useRef(deps)
    depsRef.current = deps

    React.useEffect(() => {
        return () => {
            promiseStateCancel(promiseState)
        }
    }, [])

    const lens: StateLens<PromiseState<V>> = {
        get: () => promiseStateRef.current,
        set: async (newState) => setPromiseState(newState),
    }

    return {
        ...createPromiseManager(() => depsRef.current, lens),
        promiseState,
    }
}

export function usePromiseManagerOnLoad<V>(deps: CommonDeps, fn: () => Promise<V>) {
    const pm = usePromiseManager<V>(deps)

    React.useEffect(() => {
        pm.trigger(fn)
    }, [])

    return pm
}
